/**
 * Browser push plumbing — the thin layer between the Notification center UI and the
 * Push API. Nothing here talks to GraphQL; the component owns that.
 *
 * Mental model: we never deliver a notification. The browser mints a SUBSCRIPTION (an
 * endpoint URL on its own push service, plus two keys), we store it, and the server hands
 * an encrypted payload to that endpoint. So a subscription belongs to a BROWSER, not to a
 * person — which is why the UI calls the master toggle "this browser".
 */

/** Push needs a service worker AND the Push API AND the Notification API. Safari on
 *  older macOS has SW but no PushManager, so check all three. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function permissionState(): NotificationPermission | 'unsupported' {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

/** The VAPID public key arrives as base64url text; applicationServerKey wants raw bytes.
 *  Returns the ArrayBuffer rather than the view: since TS 5.7 Uint8Array is generic over
 *  its backing buffer, and `Uint8Array<ArrayBufferLike>` is not a `BufferSource`. */
function urlBase64ToBytes(base64: string): ArrayBuffer {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
}

/** Register (idempotent — the browser dedupes by URL) and wait until it is usable. */
async function registration(): Promise<ServiceWorkerRegistration> {
  await navigator.serviceWorker.register('/sw.js');
  return navigator.serviceWorker.ready;
}

/** This browser's existing subscription, or null. Does NOT register the worker — safe to
 *  call on boot without side effects for someone who never opted in. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export interface SubscriptionPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent: string;
}

/** Shape a PushSubscription for the API. The keys come back as ArrayBuffers. */
export function toPayload(sub: PushSubscription): SubscriptionPayload {
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  return {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh ?? '',
    auth: json.keys?.auth ?? '',
    userAgent: navigator.userAgent.slice(0, 200),
  };
}

/**
 * Ask for permission and subscribe. MUST be called from a user gesture (a click) —
 * browsers ignore requestPermission() otherwise.
 *
 * Returns null when the user declines. 'denied' is STICKY: the site can never re-prompt
 * and cannot reset it, so the caller has to render an explanation rather than a button
 * that would do nothing.
 */
export async function enablePush(vapidPublicKey: string): Promise<SubscriptionPayload | null> {
  if (!isPushSupported()) return null;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const reg = await registration();
  const existing = await reg.pushManager.getSubscription();
  if (existing) return toPayload(existing);

  const sub = await reg.pushManager.subscribe({
    // Required by Chrome: a subscription that can receive a "silent" push is refused.
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToBytes(vapidPublicKey),
  });
  return toPayload(sub);
}

/** Drop this browser's subscription. Returns the endpoint that was dropped so the caller
 *  can tell the server which row to delete. Best-effort: a browser-side failure must not
 *  stop us removing the server row, or we would keep pushing to a dead endpoint. */
export async function disablePush(): Promise<string | null> {
  const sub = await currentSubscription();
  if (!sub) return null;
  const { endpoint } = sub;
  await sub.unsubscribe().catch(() => undefined);
  return endpoint;
}
